/**
 * The pure half of the crash reporter (plan D48, T69): what a caught error
 * becomes, what it may not carry, and whether it is sent at all.
 *
 * Nothing here reads a clock, the location or the platform. The caller passes
 * them in `ReportContext`, so every rule below is testable in Node with a
 * pinned `now`.
 *
 * ── THE BODY GOES THROUGH THE CONTRACT ──────────────────────────────────────
 * `clientErrorInput` is strict, and a beacon never learns it was refused. A
 * report is therefore built by `@repo/client/data/client-errors` (which cuts
 * every field to its cap) and then PARSED here, so a mismatch between the app
 * and the contract is a failing test in this package, never a report the
 * server drops in silence.
 *
 * ── REDACTION ───────────────────────────────────────────────────────────────
 * A WebSocket URL in an error message carries a single-use ticket (D34), and a
 * failed `fetch` can echo a bearer. Both are cut from the message, the stack
 * and the route before anything leaves the device. That is all the
 * personal-data policy there is: the report carries the error's own text and
 * the screen state, never the account's email.
 *
 * ── DEDUP AND THROTTLE ──────────────────────────────────────────────────────
 * A render loop throws the same error many times a second, and React's
 * StrictMode plus Vite's overlay throw it twice in development. The gate lets
 * one report per message and top stack frame through per minute, and at most
 * twenty per page session. The server rate-limits too, but that limit is per
 * address: a phone looping on a crash would burn its data and lock every
 * report from the same network out for ten minutes.
 */

import { buildClientErrorReport } from "@repo/client/data/client-errors";
import {
  type ClientErrorInput,
  type ClientErrorPlatform,
  type ClientErrorSource,
  clientErrorInput,
} from "@repo/contract";

/** One report per message + top frame inside this window. */
export const DEDUP_WINDOW_MS = 60_000;

/** Reports per page session, whatever they are. */
export const MAX_REPORTS_PER_SESSION = 20;

export interface ReportContext {
  readonly bundleVersion: string;
  readonly now: Date;
  /** What `getPlatform()` answered; anything but `android` reports as `web`. */
  readonly platform: string;
  /** `location.search` — it carries the screen state. */
  readonly route?: string | undefined;
}

// ── Redaction ────────────────────────────────────────────────────────────────

const REDACTED = "[redacted]";

/** `ticket=…` (D34) and the other credential-bearing query parameters. */
const CREDENTIAL_PARAM =
  /\b((?:ticket|token|access_token|refresh_token|id_token)=)[^&#\s"'<>]+/gi;

/** `Bearer <token>`, wherever it was echoed. */
const BEARER = /\b(bearer\s+)[\w.~+/-]+=*/gi;

/** A JWT on its own: three base64url segments, the first a JSON header. */
const JWT = /\beyJ[\w-]+\.[\w-]+\.[\w-]*/g;

export function redact(text: string): string {
  return text
    .replace(CREDENTIAL_PARAM, `$1${REDACTED}`)
    .replace(BEARER, `$1${REDACTED}`)
    .replace(JWT, REDACTED);
}

// ── Describing what was thrown ───────────────────────────────────────────────

export interface DescribedError {
  readonly message: string;
  readonly stack?: string | undefined;
}

/**
 * A `JSON.stringify` that cannot throw: a cyclic reference is written as
 * `"[cycle]"`, a BigInt as its digits, and anything that still fails falls
 * back to the object's tag.
 */
export function safeStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  try {
    const text = JSON.stringify(value, (_key, inner: unknown) => {
      if (typeof inner === "bigint") {
        return inner.toString();
      }
      if (typeof inner === "object" && inner !== null) {
        if (seen.has(inner)) {
          return "[cycle]";
        }
        seen.add(inner);
      }
      return inner;
    });
    return text ?? String(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
}

function stringField(value: object, key: string): string | undefined {
  try {
    const field: unknown = Reflect.get(value, key);
    return typeof field === "string" ? field : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The message and stack of anything a script can throw or reject with: an
 * `Error` (an `ApiError` included), a string, a number, a plain object, a
 * cyclic one, `null`.
 */
export function describeThrown(thrown: unknown): DescribedError {
  if (typeof thrown === "string") {
    return { message: thrown };
  }
  if (typeof thrown === "object" && thrown !== null) {
    const message = stringField(thrown, "message");
    if (message !== undefined) {
      const name = stringField(thrown, "name");
      const stack = stringField(thrown, "stack");
      return {
        message:
          name !== undefined && name !== "" && name !== "Error"
            ? `${name}: ${message}`
            : message,
        stack,
      };
    }
    return { message: safeStringify(thrown) };
  }
  try {
    return { message: String(thrown) };
  } catch {
    return { message: typeof thrown };
  }
}

// ── The report ───────────────────────────────────────────────────────────────

export function toContractPlatform(platform: string): ClientErrorPlatform {
  return platform === "android" ? "android" : "web";
}

/**
 * The contract body for one caught error: redacted, cut to the caps, parsed
 * against `clientErrorInput`. Throws only if this file and the contract
 * disagree — the reporter swallows that, and the test suite catches it.
 */
export function toClientError(
  source: ClientErrorSource,
  error: unknown,
  context: ReportContext
): ClientErrorInput {
  const described = describeThrown(error);
  const route = context.route === undefined ? undefined : redact(context.route);
  return clientErrorInput.parse(
    buildClientErrorReport({
      bundleVersion: context.bundleVersion,
      message: redact(described.message) || "(no message)",
      occurredAt: context.now,
      platform: toContractPlatform(context.platform),
      route,
      source,
      stack:
        described.stack === undefined ? undefined : redact(described.stack),
    })
  );
}

// ── Dedup and throttle ───────────────────────────────────────────────────────

/** V8's frame line. */
const V8_FRAME = /^\s*at\s/;

/** Firefox/WebKit's `fn@url:line:col` (these stacks carry no message line). */
const AT_SIGN_FRAME = /@.*:\d+/;

/**
 * The first frame of a stack, or "" when there is none. V8 frames are looked
 * for first: a V8 stack opens with the message, and a message holding an `@`
 * would otherwise read as a Firefox frame.
 */
export function topFrame(stack: string | undefined): string {
  if (stack === undefined) {
    return "";
  }
  const lines = stack.split("\n");
  const frame =
    lines.find((line) => V8_FRAME.test(line)) ??
    lines.find((line) => AT_SIGN_FRAME.test(line));
  return frame === undefined ? "" : frame.trim();
}

export function dedupKey(report: ClientErrorInput): string {
  return `${report.message}\n${topFrame(report.stack)}`;
}

/**
 * A view transition that a later navigation SKIPPED. Not an application
 * error, and not catchable by any application code.
 *
 * `defaultViewTransition: true` started a transition for every navigation,
 * and a second navigation landing while the first was still capturing
 * skipped the first. The skipped one's `ready` promise rejects, and the
 * router hands the app only `updateCallbackDone` — so the rejection reaches
 * `window` with no `catch` anywhere it could have been written. The router
 * starts none since `D190`; this stays because one navigation that opts back
 * in (`viewTransition: true`) would bring the report back with it.
 *
 * Measured on the owner's Galaxy S22 (WebView 153) on 2026-09-22: one of
 * these on every cold launch onto a URL that carries a layer, 330 ms after
 * the process started, at ERROR level. Unfiltered it costs one of the twenty
 * reports a page session may send, on every launch, for ever — and the
 * per-minute dedup cannot help, because it happens once per session.
 *
 * Matched on the DOM's own wording, not on the name: a `fetch` the app aborts
 * rejects with an `AbortError` too and must still be reported. The sibling
 * `TimeoutError: Transition was aborted because of timeout in DOM update` is
 * deliberately NOT matched — a DOM update that took longer than the browser's
 * limit says something true about the app, and a skipped transition says
 * only that the person navigated twice.
 */
export function isSkippedViewTransition(thrown: unknown): boolean {
  const { message } = describeThrown(thrown);
  return (
    message.startsWith("AbortError: ") &&
    message.includes("Transition was skipped")
  );
}

export interface ReportGate {
  /** True when this report may be sent; records it as sent if so. */
  admit(report: ClientErrorInput, now: Date): boolean;
}

export function createReportGate(options?: {
  maxPerSession?: number;
  windowMs?: number;
}): ReportGate {
  const windowMs = options?.windowMs ?? DEDUP_WINDOW_MS;
  const maxPerSession = options?.maxPerSession ?? MAX_REPORTS_PER_SESSION;
  // Never more than `maxPerSession` entries: only admitted reports are kept.
  const lastSentAt = new Map<string, number>();
  let sent = 0;
  return {
    admit(report, now) {
      if (sent >= maxPerSession) {
        return false;
      }
      const key = dedupKey(report);
      const at = now.getTime();
      const previous = lastSentAt.get(key);
      if (previous !== undefined && at - previous < windowMs) {
        return false;
      }
      lastSentAt.set(key, at);
      sent += 1;
      return true;
    },
  };
}
